// A big map drawn from its pyramid (issue 1172): one layer of 512-pixel tile sprites, kept to what `pyramidPlan` says
// for the view. Finer levels lie over coarser ones, so a coarser tile shows until the right one arrives. Every tile is
// fetched with the bearer (`pyramidTileUrl`), and a tile let go takes its texture (its GPU copy) and its object URL with
// it. At most `IN_FLIGHT` tiles are asked at once; each arrival plans again, so a fast pan never queues the map.
// A tile that fails (refused, lost, or a picture that will not decode) is not asked again until its backoff has run
// (1 s, doubling to 30 s; a new map starts afresh): the coarser level shows under it meanwhile. Every request carries the
// generation of the map it was made for, so an answer for a map no longer drawn — even the same map set again — never
// lands, and never clears the new map's request for the same tile.
import { Container, Sprite, Texture } from 'pixi.js';
import { pyramidBoardRect, pyramidLevels, parsePyramidKey, type BoardRect, type PyramidLevel } from '@dragons-table/vtt-shared';
import { pyramidTileUrl, releasePyramidTile } from '../assets';
import { pyramidPlan } from './pyramid';

const IN_FLIGHT = 6;
export const RETRY_FIRST_MS = 1000;
export const RETRY_MAX_MS = 30_000;

/** What the `?perf=1` readout shows of it: tiles held (on the GPU), asked, and the level drawn. */
export const pyramidStats = { held: 0, asked: 0, level: -1, fetched: 0 };

export class PyramidLayer {
  readonly container = new Container();
  private map: { id: string; width: number; height: number; levels: PyramidLevel[] } | null = null;
  private held = new Map<string, Sprite>();
  private asked = new Set<string>();
  private planned = '';
  private stale = false;
  private gone = false;
  /** Bumped with every map change: an answer from an older generation is let go, never drawn. */
  private gen = 0;
  /** Tiles that failed: when each may be asked again, and the wait after its next failure. */
  private failed = new Map<string, { at: number; wait: number; ready: boolean }>();
  /** The map whose coarsest tile is on the board, if any. */
  basePainted: string | null = null;

  /** `changed` is called when a tile arrives (the board draws again); `painted` once the base tile is on the board. */
  constructor(private readonly changed: () => void, private readonly painted: (id: string) => void, private readonly now: () => number = () => performance.now()) {
    this.container.sortableChildren = true;
  }

  /** The map to draw, or none: a different map lets every tile of the old one go. */
  set(map: { id: string; width: number; height: number } | null): void {
    if (map && this.map && map.id === this.map.id && map.width === this.map.width && map.height === this.map.height) return;
    this.clear();
    this.gen += 1;
    this.map = map ? { ...map, levels: pyramidLevels(map.width, map.height) } : null;
    this.container.visible = this.map !== null;
    this.planned = '';
  }

  get active(): boolean { return this.map !== null; }

  /** Plan for the view (board pixels on screen) at `zoom` (screen pixels per board pixel, times the pixel ratio). */
  update(view: BoardRect, zoom: number): void {
    const map = this.map;
    if (!map || this.gone) return;
    const key = `${Math.round(view.x)},${Math.round(view.y)},${Math.round(view.width)},${Math.round(view.height)},${zoom.toFixed(4)}`;
    const t = this.now();
    const due = [...this.failed.values()].some((f) => !f.ready && f.at <= t);
    if (key === this.planned && !this.stale && !due) return;
    this.planned = key; this.stale = false;
    // A tile still waiting out its backoff counts as asked: the plan neither fetches it nor drops the coarser tile under it.
    const waiting = new Set(this.asked);
    for (const [k, f] of this.failed) { if (f.at > t) waiting.add(k); else f.ready = true; } // a backoff run out plans once
    const plan = pyramidPlan(map.levels, view, zoom, new Set(this.held.keys()), waiting);
    pyramidStats.level = plan.level;
    for (const k of plan.drop) this.drop(k);
    for (const k of plan.fetch) {
      if (this.asked.size >= IN_FLIGHT) break;
      this.fetch(map, k);
    }
    pyramidStats.held = this.held.size; pyramidStats.asked = this.asked.size;
  }

  private fetch(map: { id: string; levels: PyramidLevel[] }, key: string): void {
    const p = parsePyramidKey(key);
    const level = p ? map.levels[p.k] : undefined;
    const rect = p && level ? pyramidBoardRect(level, p.x, p.y) : null;
    if (!p || !rect) return;
    const gen = this.gen;
    const current = () => !this.gone && this.gen === gen;
    this.asked.add(key);
    void (async () => {
      const url = await pyramidTileUrl(map.id, key);
      let texture: Texture | null = null;
      if (url && current()) {
        const img = new Image();
        img.src = url;
        await img.decode().catch(() => undefined);
        if (current() && img.naturalWidth > 0) texture = Texture.from(img, true);
      }
      // An answer for a map no longer drawn: its tiles were let go with it (`clear`), and its request is not ours.
      if (!current()) { texture?.destroy(true); return; }
      this.asked.delete(key);
      if (!texture) {
        if (url) releasePyramidTile(map.id, key); // a picture that will not decode: its object URL goes, and it is asked afresh later
        const wait = this.failed.get(key)?.wait ?? RETRY_FIRST_MS;
        this.failed.set(key, { at: this.now() + wait, wait: Math.min(wait * 2, RETRY_MAX_MS), ready: false });
        this.stale = true; this.changed();
        return;
      }
      this.failed.delete(key);
      const sprite = new Sprite(texture);
      sprite.position.set(rect.x, rect.y);
      sprite.width = rect.width; sprite.height = rect.height;
      sprite.zIndex = -p.k; // finer over coarser
      const had = this.held.get(key);
      if (had) { this.container.removeChild(had); had.texture.destroy(true); had.destroy(); } // superseded
      this.container.addChild(sprite);
      this.held.set(key, sprite);
      pyramidStats.fetched += 1; pyramidStats.held = this.held.size; pyramidStats.asked = this.asked.size;
      if (p.k === map.levels.length - 1) { this.basePainted = map.id; this.painted(map.id); }
      this.stale = true;
      this.changed();
    })();
  }

  private drop(key: string): void {
    const sprite = this.held.get(key);
    if (!sprite) return;
    this.held.delete(key);
    sprite.texture.destroy(true);
    sprite.destroy();
    if (this.map) releasePyramidTile(this.map.id, key);
  }

  private clear(): void {
    for (const key of [...this.held.keys()]) this.drop(key);
    if (this.map) releasePyramidTile(this.map.id);
    this.asked.clear();
    this.failed.clear();
    this.basePainted = null;
    pyramidStats.held = 0; pyramidStats.asked = 0; pyramidStats.level = -1;
  }

  destroy(): void {
    this.clear();
    this.map = null;
    this.gen += 1;
    this.gone = true;
  }
}
